// 主機服務（批169）的顯示規則——主機詳情頁〈主機服務〉卡用。
//
// ★ 這裡**不寫任何服務名字面**：服務名、中文名與預設 channel 都是 agent 在心跳回報的
//   （`HostService.service`／`label`／`default_channel`），LS 只存、只照印。這支檔只翻譯
//   「狀態」與「問題碼」這兩組**通用**的值域（任何主機服務都用同一組）。

import type { HostService, HostServiceAction, HostServiceIssue } from '@/api/hosting'

export const HOST_SERVICE_STATE_LABELS: Record<string, string> = {
  not_installed: '未安裝',
  running: '運行中',
  stopped: '已停止',
  unhealthy: '異常',
  unknown: '狀態不明',
}

/** 狀態的中文（不認得的值原樣顯示——agent 比前端新是常態）。 */
export function hostServiceStateLabel(svc: Pick<HostService, 'state' | 'installed'>): string {
  const state = svc.state || (svc.installed ? 'unknown' : 'not_installed')
  return HOST_SERVICE_STATE_LABELS[state] ?? state
}

/** `status-pill` 的色階（沿用 `lib/fleet.ts` 站台狀態那一組 class）。 */
export function hostServiceStateClass(svc: Pick<HostService, 'state' | 'installed'>): string {
  const state = svc.state || (svc.installed ? 'unknown' : 'not_installed')
  return (
    {
      running: 'active',
      stopped: 'superseded',
      unhealthy: 'revoked',
      unknown: 'pending',
      not_installed: '',
    }[state] ?? 'pending'
  )
}

export const HOST_SERVICE_ISSUE_LABELS: Record<string, string> = {
  dir_missing: '站目錄還沒建',
  dir_not_directory: '站目錄位置被別的檔案佔用（agent 不會動它）',
  dir_owner: '站目錄擁有者不對（下次安裝／升級時原地修正）',
  dir_mode: '站目錄權限不是 0700',
  env_missing: '站台版本還沒有這個掛載（升級站台後接上）',
  env_mismatch: '站台設定指到別的目錄',
  socket_missing: '服務還沒在這一站建立連線',
  protocol_mismatch: '服務與 agent 的協定版本不一致（請升級 agent）',
}

/** 一筆問題的中文（哪一站＋原因；不認得的碼原樣顯示）。 */
export function hostServiceIssueText(issue: HostServiceIssue): string {
  const reason = HOST_SERVICE_ISSUE_LABELS[issue.code] ?? issue.code
  const where = issue.site ? `${issue.site}：` : ''
  const detail = issue.detail ? `（${issue.detail}）` : ''
  return `${where}${reason}${detail}`
}

/** 這個服務現在能按哪幾顆（沒裝＝安裝；裝了＝升級＋移除）。 */
export function hostServiceActions(svc: Pick<HostService, 'installed'>): HostServiceAction[] {
  return svc.installed ? ['upgrade', 'remove'] : ['install']
}

export const HOST_SERVICE_ACTION_LABELS: Record<HostServiceAction, string> = {
  install: '安裝',
  upgrade: '升級',
  remove: '移除',
}

/** 已接上幾站（`2 / 3`；agent 沒回報＝`—`）。 */
export function hostServiceSitesText(svc: Pick<HostService, 'sites_attached' | 'sites_total'>): string {
  if (svc.sites_total == null) return '—'
  return `${svc.sites_attached ?? 0} / ${svc.sites_total}`
}

/** 版本與 channel 的輸入檢查（與後端 `HostServiceIn` 同一組形狀）；空字串＝不帶。回錯誤訊息或 null。 */
export function hostServiceInputError(version: string, channel: string): string | null {
  const v = version.trim()
  const c = channel.trim()
  if (v && !/^\d+\.\d+\.\d+$/.test(v)) return '版本要是 x.y.z（留空＝最新版）'
  if (c && !/^[a-z0-9][a-z0-9-]{0,31}$/.test(c)) return '更新通道只能用小寫英數與連字號（留空＝服務預設）'
  return null
}
